import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from './useAuth.js'

/**
 * The "frequently bought together" offers themselves.
 *
 * Separate from `useMarketingSettings` because they are a different kind of
 * thing: the settings are one object saved whole, and these are rows created and
 * deleted one at a time. Sharing a hook would mean an unsaved heading blocking a
 * delete, or a delete discarding a half-typed heading.
 */

export interface BundleItem {
  variant_id: string
  product_id: string | null
  handle: string | null
  title: string | null
  image: string | null
}

export interface Bundle {
  id: string
  name: string
  active: boolean
  main_variant_id: string | null
  main_product_id: string | null
  main_title: string | null
  items: BundleItem[]
  discount_type: 'fixed' | 'percent'
  discount_value: number
  button_text: string
  default_selected: boolean
  max_products: number
  starts_at: string | null
  ends_at: string | null
  /** Computed by the server: active, in its date window, and holding products. */
  live: boolean
  discount_label: string
}

/** What the form posts. Everything the server will not let a merchant set is absent. */
export type BundleDraft = Omit<Bundle, 'id' | 'live' | 'discount_label'>

export interface VariantHit {
  variant_id: string
  product_id: string
  handle: string
  title: string
  variant_title: string | null
  sku: string
  price: number
  image: string | null
  available: boolean
}

export function useBundles() {
  const { shop } = useAuth()
  const queryClient = useQueryClient()

  const key = ['marketing-bundles', shop?.id]
  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: key })

  const query = useQuery({
    queryKey: key,
    queryFn: () => api<Bundle[]>('/v1/marketing/bundles'),
    enabled: Boolean(shop?.id),
  })

  const create = useMutation({
    mutationFn: (body: BundleDraft) => api<Bundle>('/v1/marketing/bundles', { method: 'POST', body }),
    onSuccess: refresh,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: BundleDraft }) =>
      api<Bundle>(`/v1/marketing/bundles/${id}`, { method: 'PUT', body }),
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api<{ deleted: boolean }>(`/v1/marketing/bundles/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  })

  return {
    bundles: query.data ?? [],
    isPending: query.isPending,
    error: query.error,
    create,
    update,
    remove,
    // One message for the drawer's footer, whichever write failed.
    writeError: create.error ?? update.error ?? remove.error,
    saving: create.isPending || update.isPending,
  }
}

/**
 * The product picker's results.
 *
 * Debounced, because this runs on every keystroke and each miss is a Shopify
 * GraphQL call against the merchant's own rate limit. 250ms is short enough to
 * feel immediate and long enough that typing a word is one request, not six.
 *
 * `keepPreviousData` is deliberate: without it the list empties between
 * keystrokes and the picker flickers through blank states as somebody types.
 */
export function useProductSearch(term: string, enabled: boolean) {
  const { shop } = useAuth()
  const [debounced, setDebounced] = useState(term)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 250)
    return () => clearTimeout(timer)
  }, [term])

  return useQuery({
    queryKey: ['bundle-products', shop?.id, debounced],
    queryFn: () => api<VariantHit[]>('/v1/marketing/bundles/products', { query: { q: debounced } }),
    enabled: enabled && Boolean(shop?.id),
    placeholderData: (previous) => previous,
    // The catalogue does not change while a merchant fills in one form.
    staleTime: 60_000,
  })
}
